import type { GeographicRing } from '../utils/coverageGlobe';

export interface LandingCoverageRegulation {
  consolidatedAt?: string;
  descriptionKey?: string;
  href: string;
  nameKey: string;
}

export interface LandingCoverageCountry {
  code: string;
  flag: string;
  labelKey: string;
  latitude: number;
  longitude: number;
  regulations: readonly LandingCoverageRegulation[];
  status: 'integrated' | 'pending';
  svgRings?: readonly GeographicRing[];
}

export interface LandingCoverageRoute {
  from: string;
  to: string;
}

// Single source of truth for the fallback SVG, the Three.js network and the
// country descriptions. Add the next country here; svgRings may be omitted
// until its simplified Natural Earth outline is available.
export const LANDING_COVERAGE_COUNTRIES: readonly LandingCoverageCountry[] = [
  {
    code: 'FRA',
    flag: '🇫🇷',
    labelKey: 'coverageMap.france',
    latitude: 48.8566,
    longitude: 2.3522,
    status: 'integrated',
    regulations: [
      {
        consolidatedAt: '2026-01-05',
        descriptionKey: undefined,
        href: 'https://www.ecologie.gouv.fr/sites/default/files/documents/Arr%C3%AAt%C3%A9%20du%2029%20mai%202009%20consolid%C3%A9%20%2805-01-2026%29.pdf',
        nameKey: 'coverageMap.frenchRegulation',
      },
      {
        descriptionKey: undefined,
        href: 'https://www.legifrance.gouv.fr/download/file/DAVXDsJWxZnHIfhKZ6xActxJZvxKXBoNLUNa3sUly-c=/JOE_TEXTE',
        nameKey: 'coverageMap.frenchAmendingOrder2026',
      },
    ],
    svgRings: [
      [[6.19, 49.46], [6.66, 49.2], [8.1, 49.02], [7.59, 48.33], [7.47, 47.62], [7.19, 47.45], [6.74, 47.54], [6.77, 47.29], [6.04, 46.73], [6.02, 46.27], [6.5, 46.43], [6.84, 45.99], [6.8, 45.71], [7.1, 45.33], [6.75, 45.03], [7.01, 44.25], [7.55, 44.13], [7.44, 43.69], [6.53, 43.13], [4.56, 43.4], [3.1, 43.07], [2.99, 42.47], [1.83, 42.34], [0.7, 42.8], [0.34, 42.58], [-1.5, 43.03], [-1.9, 43.42], [-1.38, 44.02], [-1.19, 46.01], [-2.23, 47.06], [-2.96, 47.57], [-4.49, 47.95], [-4.59, 48.68], [-3.3, 48.9], [-1.62, 48.64], [-1.94, 49.78], [-0.99, 49.35], [1.34, 50.13], [1.64, 50.95], [2.51, 51.15], [2.66, 50.8], [3.12, 50.78], [3.59, 50.38], [4.29, 49.91], [4.8, 49.99], [5.68, 49.53], [5.9, 49.44], [6.19, 49.46]],
      [[8.75, 42.63], [9.39, 43.01], [9.56, 42.15], [9.23, 41.38], [8.78, 41.58], [8.54, 42.26], [8.75, 42.63]],
    ],
  },
  {
    code: 'DEU',
    flag: '🇩🇪',
    labelKey: 'coverageMap.germany',
    latitude: 52.52,
    longitude: 13.405,
    status: 'integrated',
    regulations: [
      {
        descriptionKey: 'coverageMap.germanGgavDescription',
        href: 'https://www.gesetze-im-internet.de/ggav_2002/GGAV_2002.pdf',
        nameKey: 'coverageMap.germanGgavName',
      },
      {
        descriptionKey: 'coverageMap.germanGgvsebDescription',
        href: 'https://www.gesetze-im-internet.de/ggvseb/GGVSEB.pdf',
        nameKey: 'coverageMap.germanGgvsebName',
      },
      {
        descriptionKey: 'coverageMap.germanRsebDescription',
        href: 'https://www.bmv.de/SharedDocs/DE/Anlage/G/Gefahrengut/rseb-2025.pdf?__blob=publicationFile',
        nameKey: 'coverageMap.germanRsebName',
      },
    ],
    svgRings: [
      [[14.12, 53.76], [14.35, 53.25], [14.07, 52.98], [14.44, 52.63], [14.69, 52.09], [14.61, 51.75], [15.02, 51.11], [14.57, 51], [14.31, 51.12], [14.06, 50.93], [13.34, 50.73], [12.97, 50.48], [12.24, 50.27], [12.41, 49.97], [12.52, 49.55], [13.03, 49.31], [13.6, 48.88], [13.24, 48.42], [12.88, 48.29], [13.03, 47.64], [12.93, 47.47], [12.62, 47.67], [12.14, 47.7], [11.42, 47.52], [10.54, 47.57], [10.4, 47.3], [9.89, 47.58], [9.6, 47.52], [8.52, 47.83], [8.32, 47.61], [7.47, 47.62], [7.59, 48.33], [8.1, 49.02], [6.66, 49.2], [6.19, 49.46], [6.24, 49.9], [6.04, 50.13], [6.16, 50.8], [5.99, 51.85], [6.59, 51.85], [6.84, 52.23], [7.09, 53.14], [6.91, 53.48], [7.1, 53.69], [7.94, 53.75], [8.12, 53.53], [8.8, 54.02], [8.57, 54.4], [8.53, 54.96], [9.28, 54.83], [9.92, 54.98], [9.94, 54.6], [10.95, 54.36], [10.94, 54.01], [11.96, 54.2], [12.52, 54.47], [13.65, 54.08], [14.12, 53.76]],
    ],
  },
  {
    code: 'BEL',
    flag: '🇧🇪',
    labelKey: 'coverageMap.belgium',
    latitude: 50.8503,
    longitude: 4.3517,
    regulations: [],
    status: 'pending',
    svgRings: [
      [[6.16, 50.8], [6.04, 50.13], [5.78, 50.09], [5.68, 49.53], [4.8, 49.99], [4.29, 49.91], [3.59, 50.38], [3.12, 50.78], [2.66, 50.8], [2.51, 51.15], [3.31, 51.35], [4.05, 51.27], [4.97, 51.47], [5.61, 51.04], [6.16, 50.8]],
    ],
  },
  {
    code: 'NLD',
    flag: '🇳🇱',
    labelKey: 'coverageMap.netherlands',
    latitude: 52.3676,
    longitude: 4.9041,
    regulations: [],
    status: 'pending',
    svgRings: [
      [[6.91, 53.48], [7.09, 53.14], [6.84, 52.23], [6.59, 51.85], [5.99, 51.85], [6.16, 50.8], [5.61, 51.04], [4.97, 51.47], [4.05, 51.27], [3.31, 51.35], [3.83, 51.62], [4.71, 53.09], [6.08, 53.51], [6.91, 53.48]],
    ],
  },
  {
    code: 'ESP',
    flag: '🇪🇸',
    labelKey: 'coverageMap.spain',
    latitude: 40.4168,
    longitude: -3.7038,
    regulations: [],
    status: 'pending',
    svgRings: [
      [[-7.45, 37.1], [-7.54, 37.43], [-7.17, 37.8], [-7.03, 38.08], [-7.37, 38.37], [-7.1, 39.03], [-7.5, 39.63], [-7.07, 39.71], [-7.03, 40.19], [-6.86, 40.33], [-6.85, 41.11], [-6.39, 41.38], [-6.67, 41.88], [-7.25, 41.92], [-7.42, 41.79], [-8.01, 41.79], [-8.26, 42.28], [-8.67, 42.14], [-9.03, 41.88], [-8.98, 42.59], [-9.39, 43.03], [-7.98, 43.75], [-6.76, 43.57], [-5.41, 43.57], [-4.35, 43.4], [-3.52, 43.46], [-1.9, 43.42], [-1.5, 43.03], [0.34, 42.58], [0.7, 42.8], [1.83, 42.34], [2.99, 42.47], [3.04, 41.89], [2.09, 41.23], [0.81, 41.01], [0.72, 40.68], [0.11, 40.12], [-0.28, 39.31], [0.11, 38.74], [-0.47, 38.29], [-0.68, 37.64], [-1.44, 37.44], [-2.15, 36.67], [-3.41, 36.66], [-4.37, 36.68], [-5, 36.32], [-5.38, 35.95], [-5.87, 36.03], [-6.24, 36.37], [-6.52, 36.94], [-7.45, 37.1]],
    ],
  },
  {
    code: 'ITA',
    flag: '🇮🇹',
    labelKey: 'coverageMap.italy',
    latitude: 41.9028,
    longitude: 12.4964,
    regulations: [
      {
        descriptionKey: 'coverageMap.italianCircular13921Description',
        href: 'https://www.mit.gov.it/nfsmitgov/files/media/normativa/2024-05/Circolare_protocollo_13921%20del%2014-05-2024.pdf',
        nameKey: 'coverageMap.italianCircular13921Name',
      },
      {
        descriptionKey: 'coverageMap.italianDecree2023Description',
        href: 'https://www.gazzettaufficiale.it/eli/gu/2023/09/20/220/sg/pdf',
        nameKey: 'coverageMap.italianDecree2023Name',
      },
      {
        descriptionKey: 'coverageMap.italianDecree2025Description',
        href: 'https://www.gazzettaufficiale.it/do/gazzetta/downloadPdf?dataPubblicazioneGazzetta=20250319&edizione=0&estensione=pdf&numeroGazzetta=65&numeroSupplemento=0&progressivo=0&tipoSerie=SG&tipoSupplemento=GU',
        nameKey: 'coverageMap.italianDecree2025Name',
      },
      {
        descriptionKey: 'coverageMap.italianDlgs35Description',
        href: 'https://www.gazzettaufficiale.it/eli/gu/2010/03/11/58/sg/pdf',
        nameKey: 'coverageMap.italianDlgs35Name',
      },
    ],
    status: 'integrated',
    svgRings: [
      [[10.44, 46.89], [11.05, 46.75], [11.17, 46.94], [12.15, 47.11], [12.38, 46.77], [13.81, 46.51], [13.7, 46.02], [13.94, 45.59], [13.14, 45.74], [12.33, 45.38], [12.38, 44.89], [12.26, 44.6], [12.59, 44.09], [13.53, 43.59], [14.03, 42.76], [15.14, 41.96], [15.92, 41.96], [16.17, 41.74], [15.89, 41.54], [16.79, 41.18], [17.52, 40.88], [18.38, 40.36], [18.48, 40.17], [18.29, 39.81], [17.74, 40.28], [16.87, 40.44], [16.45, 39.8], [17.17, 39.43], [17.05, 38.9], [16.63, 38.84], [16.1, 37.99], [15.68, 37.91], [15.69, 38.22], [15.89, 38.75], [16.11, 38.96], [15.72, 39.54], [15.41, 40.05], [15, 40.17], [14.7, 40.61], [14.06, 40.79], [13.63, 41.19], [12.89, 41.25], [12.11, 41.71], [11.19, 42.36], [10.51, 42.93], [10.2, 43.92], [9.7, 44.04], [8.89, 44.37], [8.43, 44.23], [7.85, 43.77], [7.44, 43.69], [7.55, 44.13], [7.01, 44.25], [6.75, 45.03], [7.1, 45.33], [6.8, 45.71], [6.84, 45.99], [7.27, 45.78], [7.76, 45.82], [8.32, 46.16], [8.49, 46], [8.97, 46.04], [9.18, 46.44], [9.92, 46.31], [10.36, 46.48], [10.44, 46.89]],
      [[14.76, 38.14], [15.52, 38.23], [15.16, 37.44], [15.31, 37.13], [15.1, 36.62], [14.33, 37], [13.83, 37.1], [12.43, 37.61], [12.57, 38.13], [13.74, 38.04], [14.76, 38.14]],
      [[8.71, 40.9], [9.21, 41.21], [9.81, 40.5], [9.67, 39.18], [9.21, 39.24], [8.81, 38.91], [8.43, 39.17], [8.39, 40.38], [8.16, 40.95], [8.71, 40.9]],
    ],
  },
  {
    code: 'NOR',
    flag: '🇳🇴',
    labelKey: 'coverageMap.norway',
    latitude: 59.9139,
    longitude: 10.7522,
    regulations: [],
    status: 'pending',
  },
];

// Routes are explicit so changing the country display order never changes the network.
export const LANDING_COVERAGE_ROUTES: readonly LandingCoverageRoute[] = [
  { from: 'FRA', to: 'DEU' },
  { from: 'FRA', to: 'BEL' },
  { from: 'BEL', to: 'NLD' },
  { from: 'FRA', to: 'ESP' },
  { from: 'FRA', to: 'ITA' },
  { from: 'DEU', to: 'NOR' },
];

export function getLandingCoverageCountry(code: string): LandingCoverageCountry | undefined {
  return LANDING_COVERAGE_COUNTRIES.find((country) => country.code === code);
}
